import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';
import { DateInput } from './reports/DateInput';

const API_BASE = '/api/campaigns';

const TYPES = [
  { key: '', label: 'Tüm Tipler' },
  { key: 'N_PLUS_M', label: 'N+M (Hediyeli)' },
  { key: 'DISCOUNT_PCT', label: 'Yüzde İndirim' },
  { key: 'FREE_GIFT', label: 'Bedelsiz Ürün' },
  { key: 'BUNDLE', label: 'Paket' },
];

const TYPE_LABEL = Object.fromEntries(TYPES.filter(t => t.key).map(t => [t.key, t.label]));

const ACTIVE_TABS = [
  { key: '', label: 'Hepsi' },
  { key: 'true', label: 'Aktif' },
  { key: 'false', label: 'Pasif' },
];

const PAGE_SIZE_DEFAULT = 20;

const EMPTY_FORM = {
  code: '',
  name: '',
  shortDescription: '',
  type: 'N_PLUS_M',
  applicableProductIdsCsv: '',
  freeProductIdsCsv: '',
  thresholdQty: 0,
  freeQty: 0,
  discountPct: 0,
  isPreConditional: false,
  startDate: '',
  endDate: '',
  isActive: true,
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    inactiveCount: pick(data, 'inactiveCount', 'InactiveCount') ?? 0,
  };
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
}

function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}

function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

function typePill(type) {
  const tones = {
    N_PLUS_M: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e' },
    DISCOUNT_PCT: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8' },
    FREE_GIFT: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.30)', color: '#0f5132' },
    BUNDLE: { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.30)', color: '#5b21b6' },
  };
  const t = tones[type] || { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 999, fontWeight: 700, fontSize: 11,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
    }}>
      {TYPE_LABEL[type] || type || '-'}
    </span>
  );
}

function activePill(isActive) {
  if (isActive) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
        background: 'rgba(0, 200, 117, 0.12)', border: '1px solid rgba(0, 200, 117, 0.30)', color: '#0f5132',
      }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
        Aktif
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      background: 'rgba(100, 116, 139, 0.14)', border: '1px solid rgba(100, 116, 139, 0.28)', color: '#64748b',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
      Pasif
    </span>
  );
}

function badge(label, value, tone) {
  const tones = {
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

function countCsvIds(csv) {
  if (!csv) return 0;
  return csv.split(',').filter(s => s.trim().length > 0).length;
}

export function CampaignsPageLegacy() {
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, inactiveCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      if (activeFilter) params.set('isActive', activeFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ activeCount: data.activeCount, inactiveCount: data.inactiveCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, activeFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(EMPTY_FORM);
  };

  const openEdit = (row) => {
    setEditing({ mode: 'edit', id: pick(row, 'id', 'Id') });
    setForm({
      code: pick(row, 'code', 'Code') || '',
      name: pick(row, 'name', 'Name') || '',
      shortDescription: pick(row, 'shortDescription', 'ShortDescription') || '',
      type: pick(row, 'type', 'Type') || 'N_PLUS_M',
      applicableProductIdsCsv: pick(row, 'applicableProductIdsCsv', 'ApplicableProductIdsCsv') || '',
      freeProductIdsCsv: pick(row, 'freeProductIdsCsv', 'FreeProductIdsCsv') || '',
      thresholdQty: pick(row, 'thresholdQty', 'ThresholdQty') ?? 0,
      freeQty: pick(row, 'freeQty', 'FreeQty') ?? 0,
      discountPct: pick(row, 'discountPct', 'DiscountPct') ?? 0,
      isPreConditional: pick(row, 'isPreConditional', 'IsPreConditional') ?? false,
      startDate: toLocalDateInput(pick(row, 'startDate', 'StartDate')),
      endDate: toLocalDateInput(pick(row, 'endDate', 'EndDate')),
      isActive: pick(row, 'isActive', 'IsActive') ?? true,
    });
  };

  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submitForm = async () => {
    if (!form.code.trim()) { showToast('error', 'Code zorunlu.'); return; }
    if (!form.name.trim()) { showToast('error', 'Ad zorunlu.'); return; }
    if (!form.type) { showToast('error', 'Tip zorunlu.'); return; }

    setSaving(true);
    try {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        shortDescription: form.shortDescription.trim() || null,
        type: form.type,
        applicableProductIdsCsv: form.applicableProductIdsCsv.trim() || null,
        freeProductIdsCsv: form.freeProductIdsCsv.trim() || null,
        thresholdQty: Number(form.thresholdQty) || 0,
        freeQty: Number(form.freeQty) || 0,
        discountPct: Number(form.discountPct) || 0,
        isPreConditional: !!form.isPreConditional,
        startDate: fromLocalDateInput(form.startDate),
        endDate: fromLocalDateInput(form.endDate),
        isActive: !!form.isActive,
      };
      const url = editing.mode === 'edit' ? `${API_BASE}/${editing.id}` : API_BASE;
      const method = editing.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', editing.mode === 'edit' ? 'Kampanya güncellendi.' : 'Kampanya oluşturuldu.');
      closeEdit();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    const id = pick(deleteRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Kampanya silindi.');
      setDeleteRow(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'code', 'Code'),
        pick(row, 'name', 'Name'),
        pick(row, 'shortDescription', 'ShortDescription'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Code': return pick(row, 'code', 'Code') || '';
        case 'Name': return pick(row, 'name', 'Name') || '';
        case 'Type': return pick(row, 'type', 'Type') || '';
        case 'StartDate': {
          const v = pick(row, 'startDate', 'StartDate');
          return v ? new Date(v).getTime() : 0;
        }
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'code',
      headerName: 'Kod',
      flex: 0.7,
      minWidth: 120,
      valueGetter: ({ row }) => pick(row, 'code', 'Code') || '-',
    },
    {
      field: 'name',
      headerName: 'Ad',
      flex: 1.4,
      minWidth: 200,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700 }}>{pick(row, 'name', 'Name') || '-'}</span>
          {pick(row, 'shortDescription', 'ShortDescription') && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              {pick(row, 'shortDescription', 'ShortDescription')}
            </span>
          )}
        </div>
      ),
    },
    {
      field: 'type',
      headerName: 'Tip',
      flex: 0.9,
      minWidth: 150,
      renderCell: ({ row }) => typePill(pick(row, 'type', 'Type')),
    },
    {
      field: 'thresholdQty',
      headerName: 'Eşik',
      flex: 0.4,
      minWidth: 70,
      valueGetter: ({ row }) => pick(row, 'thresholdQty', 'ThresholdQty') ?? 0,
    },
    {
      field: 'freeQty',
      headerName: 'Bedelsiz',
      flex: 0.4,
      minWidth: 90,
      valueGetter: ({ row }) => pick(row, 'freeQty', 'FreeQty') ?? 0,
    },
    {
      field: 'discountPct',
      headerName: 'İndirim',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => {
        const v = pick(row, 'discountPct', 'DiscountPct');
        return v != null ? `%${Number(v).toFixed(2).replace(/\.00$/, '')}` : '-';
      },
    },
    {
      field: 'productsCount',
      headerName: 'Kapsam',
      flex: 0.5,
      minWidth: 90,
      sortable: false,
      valueGetter: ({ row }) => countCsvIds(pick(row, 'applicableProductIdsCsv', 'ApplicableProductIdsCsv')) + ' ürün',
    },
    {
      field: 'period',
      headerName: 'Dönem',
      flex: 0.9,
      minWidth: 160,
      sortable: false,
      valueGetter: ({ row }) =>
        `${formatDate(pick(row, 'startDate', 'StartDate'))} – ${formatDate(pick(row, 'endDate', 'EndDate'))}`,
    },
    {
      field: 'isActive',
      headerName: 'Durum',
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ row }) => activePill(pick(row, 'isActive', 'IsActive')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.9,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={() => openEdit(row)}
          >
            Düzenle
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#dc2626' }}
            onClick={() => setDeleteRow(row)}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Kampanyalar</h1>
          <p style={s.pageSubtitle}>
            Saha temsilcisinin sipariş ekranında otomatik uygulanan kampanyalar.
            Mobile master delta ile çeker; ürün GUID listeleri CSV olarak tutulur.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Aktif', counts.activeCount, 'green')}
          {badge('Pasif', counts.inactiveCount, 'gray')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
          <button type="button" style={s.btnPrimary} onClick={openCreate}>
            + Yeni Kampanya
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={s.select}
          >
            {TYPES.map(t => <option key={t.key || 'all'} value={t.key}>{t.label}</option>)}
          </select>
          <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
          {ACTIVE_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setActiveFilter(tab.key)}
              style={{
                ...(activeFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Kod / ad / açıklama..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openEdit}
        exportFileName="kampanyalar"
      />

      {editing && (
        <div style={s.overlay} onClick={closeEdit}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editing.mode === 'edit' ? 'Kampanyayı Düzenle' : 'Yeni Kampanya'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeEdit}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kod *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="örn. CAMP-2026-Q2-001"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Tip *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    style={s.select}
                  >
                    {TYPES.filter(t => t.key).map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Ad *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={s.input}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Kısa Açıklama</label>
                <input
                  type="text"
                  value={form.shortDescription}
                  onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                  style={s.input}
                />
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Eşik Adet</label>
                  <input
                    type="number"
                    min="0"
                    value={form.thresholdQty}
                    onChange={(e) => setForm({ ...form, thresholdQty: e.target.value })}
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bedelsiz Adet</label>
                  <input
                    type="number"
                    min="0"
                    value={form.freeQty}
                    onChange={(e) => setForm({ ...form, freeQty: e.target.value })}
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>İndirim %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.discountPct}
                    onChange={(e) => setForm({ ...form, discountPct: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Kapsam Ürün GUID'leri (CSV)</label>
                <textarea
                  rows={2}
                  value={form.applicableProductIdsCsv}
                  onChange={(e) => setForm({ ...form, applicableProductIdsCsv: e.target.value })}
                  placeholder="guid1,guid2,guid3"
                  style={{ ...s.textarea, fontFamily: 'monospace', fontSize: 12 }}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Bedelsiz Ürün GUID'leri (CSV)</label>
                <textarea
                  rows={2}
                  value={form.freeProductIdsCsv}
                  onChange={(e) => setForm({ ...form, freeProductIdsCsv: e.target.value })}
                  placeholder="guid1,guid2"
                  style={{ ...s.textarea, fontFamily: 'monospace', fontSize: 12 }}
                />
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Başlangıç</label>
                  <DateInput value={form.startDate} onChange={(v) => setForm({ ...form, startDate: v })} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bitiş</label>
                  <DateInput value={form.endDate} onChange={(v) => setForm({ ...form, endDate: v })} />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isPreConditional}
                    onChange={(e) => setForm({ ...form, isPreConditional: e.target.checked })}
                  />
                  Ön koşullu (eşik doluncaya kadar uygulanmaz)
                </label>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Aktif (mobile'da uygulanır)
                </label>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeEdit} disabled={saving}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={submitForm}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : (editing.mode === 'edit' ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRow && (
        <div style={s.overlay} onClick={() => setDeleteRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Kampanyayı Sil</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 14, color: '#1f2937' }}>
                <strong>{pick(deleteRow, 'name', 'Name')}</strong> kampanyasını silmek istediğinize emin misiniz?
              </div>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Mobile sipariş ekranında bu kampanya bir sonraki sync'te artık uygulanmayacak.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CampaignsPageLegacy;
